import {
  ActionIcon,
  Avatar,
  Button,
  Group,
  Loader,
  Switch,
  Text,
} from "@mantine/core";
import {
  IconExternalLink,
  IconGripVertical,
  IconPhoto,
  IconQrcode,
  IconTrash,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageLightbox } from "@/components/ImageLightbox";
import { ListCell, ListRow } from "@/components/DataList";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";

// Sürükle-bırak sıralanabilir şube satırı (kategorilerdeki SortableCategoryRow ile aynı desen).
export function SortableBranchRow({
  branch,
  sortable = true,
  tenantSlug,
  toggling = false,
  onToggle,
  onQr,
  onImage,
  onEdit,
  onManage,
  onDelete,
  deleting,
}) {
  const [zoomed, { open: openZoom, close: closeZoom }] = useDisclosure(false);
  const hasImage = Boolean(branch.imageUrl);

  // Şubenin kendisi branches:write; "Yönet" ekranı şube fiyat override'larını düzenlediği için
  // prices:update ile de açılabilir. QR salt okunur, herkese açık. Görsel marka kimliği —
  // sıralama ve silme gibi tam yetki ister.
  const { can, allBranches } = useAccess();
  const canWrite = can("branches");
  const canManage = canWrite || can("products") || can("categories");

  // Sıralama tenant genelinde tek bir liste; şube müdürü yalnızca kendi şubelerini gördüğü için
  // sürüklediğinde görmediklerinin sırasını da ezerdi. API'de de requireFullAccess.
  const canSort = sortable && allBranches;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: branch.id,
    disabled: !canSort,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    background: isDragging ? "var(--mantine-color-chocolate-0)" : undefined,
  };

  return (
    <ListRow ref={setNodeRef} style={style}>
      <ListCell w={36}>
        <ActionIcon
          variant="subtle"
          color="gray"
          {...attributes}
          {...listeners}
          {...lockProps(canSort, { title: "Sürükleyerek sırala" })}
          style={{
            cursor: canSort ? "grab" : "not-allowed",
            touchAction: "none",
          }}
        >
          <IconGripVertical size={18} />
        </ActionIcon>
      </ListCell>
      <ListCell flex>
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          {/* Görsele tıklayınca büyüyor — ürün ve kategori satırlarıyla aynı davranış.
                        Şube kartındaki fotoğraf menüde 1400×480 basılıyor ve 36px'lik avatarda
                        neye benzediği anlaşılmıyor. */}
          <Avatar
            src={branch.imageUrl}
            radius="md"
            size={36}
            onClick={hasImage ? openZoom : undefined}
            style={hasImage ? { cursor: "zoom-in" } : undefined}
            title={hasImage ? "Büyüt" : undefined}
          >
            {branch.name?.[0]?.toUpperCase()}
          </Avatar>
          {hasImage && (
            <ImageLightbox
              opened={zoomed}
              onClose={closeZoom}
              src={branch.imageUrl}
              alt={branch.name}
            />
          )}
          <Text fw={500} truncate>
            {branch.name}
          </Text>
        </Group>
      </ListCell>
      <ListCell w={70}>
        {/* Buradaki switch iyimser çevrilmiyor: yanıt gelene kadar eski konumunda kalırdı,
                    o yüzden yerini spinner alır (Ent. Durum kanal switch'leriyle aynı desen). */}
        {toggling ? (
          <Loader size="xs" color="teal" />
        ) : (
          <Switch
            checked={branch.isActive}
            color="teal"
            disabled={!canWrite}
            onChange={(event) => onToggle(branch, event.currentTarget.checked)}
          />
        )}
      </ListCell>
      <ListCell w={510} ta="right">
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          <Button
            size="compact-sm"
            variant="default"
            component="a"
            href={`https://${tenantSlug}.noxussoft.com/${branch.slug}`}
            target="_blank"
            rel="noreferrer"
            leftSection={<IconExternalLink size={14} />}
          >
            Menü
          </Button>
          <Button
            size="compact-sm"
            variant="default"
            leftSection={<IconQrcode size={14} />}
            onClick={() => onQr(branch)}
          >
            QR
          </Button>
          {/* Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor — satır düzeni herkeste aynı kalsın. */}
          <Button
            size="compact-sm"
            variant="default"
            leftSection={<IconPhoto size={14} />}
            {...lockProps(allBranches)}
            onClick={() => onImage(branch)}
          >
            Görsel
          </Button>
          <Button
            size="compact-sm"
            variant="default"
            {...lockProps(canWrite)}
            onClick={() => onEdit(branch)}
          >
            Düzenle
          </Button>
          <Button
            size="compact-sm"
            variant="light"
            {...lockProps(canManage)}
            onClick={() => onManage(branch)}
          >
            Menüyü Yönet
          </Button>
          {/* Silme cascade: eşleştirmeler + override'lar. API'de requireFullAccess. */}
          <ActionIcon
            variant="subtle"
            color="red"
            loading={deleting}
            {...lockProps(allBranches, { title: "Şubeyi sil" })}
            onClick={() => onDelete(branch)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </ListCell>
    </ListRow>
  );
}
